import type { Language } from "../api/localization";
import type { CertificationTrainingQuestionInput } from "../api/certifications";
import {
  collectDirtyUpdates,
  emptyLocDraft,
  locDraftFromStored,
  newId,
  parseChoiceIdFromKey,
  questionChoiceKey,
  questionTextKey,
  setLocValue,
  trainingSectionKey,
  trainingTitleKey,
  trainingTranscriptKey,
  trainingVideoCommentaryKey,
  type LocFieldDraft,
} from "./contentKeys";

export type { LocFieldDraft };
export { collectDirtyUpdates, setLocValue };

export type ChoiceDraft = {
  id: string;
  loc: LocFieldDraft;
};

export type QuestionDraft = {
  id: string;
  text: LocFieldDraft;
  choices: ChoiceDraft[];
  correctChoiceIndex: number;
  sortOrder: number;
};

export type TrainingLocDraft = {
  trainingId: string;
  title: LocFieldDraft;
  section: LocFieldDraft;
  transcript: LocFieldDraft;
  videoCommentary: LocFieldDraft;
  questions: QuestionDraft[];
};

export function defaultLangCode(languages: Language[], fallback = "en"): string {
  return (
    languages.find((l) => l.isDefault)?.code ??
    languages.find((l) => l.code === fallback)?.code ??
    languages[0]?.code ??
    fallback
  );
}

export function createEmptyTrainingDraft(
  trainingId: string,
  defaultLang: string,
): TrainingLocDraft {
  return {
    trainingId,
    title: emptyLocDraft(trainingTitleKey(trainingId), defaultLang),
    section: emptyLocDraft(trainingSectionKey(trainingId), defaultLang),
    transcript: emptyLocDraft(trainingTranscriptKey(trainingId), defaultLang),
    videoCommentary: emptyLocDraft(trainingVideoCommentaryKey(trainingId), defaultLang),
    questions: [],
  };
}

export function hydrateTrainingDraft(opts: {
  trainingId: string;
  title: string;
  section?: string | null;
  transcript?: string | null;
  videoCommentary?: string | null;
  questions?: Array<{
    id: string;
    text: string;
    choices: string[];
    correctChoiceIndex: number;
    sortOrder?: number;
  }>;
  defaultLang: string;
  definitions?: Record<string, string>;
}): TrainingLocDraft {
  const { trainingId, defaultLang, definitions } = opts;
  const questions: QuestionDraft[] = (opts.questions ?? []).map((q, i) => {
    const questionId = q.id || newId();
    const choices: ChoiceDraft[] = q.choices.map((choiceStored) => {
      const existingChoiceId = parseChoiceIdFromKey(choiceStored);
      const choiceId = existingChoiceId ?? newId();
      return {
        id: choiceId,
        loc: locDraftFromStored(
          choiceStored,
          questionChoiceKey(questionId, choiceId),
          defaultLang,
          definitions,
        ),
      };
    });
    // Ensure at least 2 choice slots
    while (choices.length < 2) {
      const choiceId = newId();
      choices.push({
        id: choiceId,
        loc: emptyLocDraft(questionChoiceKey(questionId, choiceId), defaultLang),
      });
    }
    return {
      id: questionId,
      text: locDraftFromStored(
        q.text,
        questionTextKey(questionId),
        defaultLang,
        definitions,
      ),
      choices,
      correctChoiceIndex: q.correctChoiceIndex,
      sortOrder: q.sortOrder ?? i,
    };
  });

  return {
    trainingId,
    title: locDraftFromStored(
      opts.title,
      trainingTitleKey(trainingId),
      defaultLang,
      definitions,
    ),
    section: locDraftFromStored(
      opts.section,
      trainingSectionKey(trainingId),
      defaultLang,
      definitions,
    ),
    transcript: locDraftFromStored(
      opts.transcript,
      trainingTranscriptKey(trainingId),
      defaultLang,
      definitions,
    ),
    videoCommentary: locDraftFromStored(
      opts.videoCommentary,
      trainingVideoCommentaryKey(trainingId),
      defaultLang,
      definitions,
    ),
    questions,
  };
}

export function emptyQuestionDraft(
  sortOrder: number,
  defaultLang: string,
): QuestionDraft {
  const questionId = newId();
  const c1 = newId();
  const c2 = newId();
  return {
    id: questionId,
    text: emptyLocDraft(questionTextKey(questionId), defaultLang),
    choices: [
      { id: c1, loc: emptyLocDraft(questionChoiceKey(questionId, c1), defaultLang) },
      { id: c2, loc: emptyLocDraft(questionChoiceKey(questionId, c2), defaultLang) },
    ],
    correctChoiceIndex: 0,
    sortOrder,
  };
}

export function allDrafts(draft: TrainingLocDraft): LocFieldDraft[] {
  const list: LocFieldDraft[] = [
    draft.title,
    draft.section,
    draft.transcript,
    draft.videoCommentary,
  ];
  for (const q of draft.questions) {
    list.push(q.text);
    for (const c of q.choices) list.push(c.loc);
  }
  return list;
}

/** Ensure default-lang values that are required get marked dirty when non-empty. */
export function markRequiredDirty(
  draft: TrainingLocDraft,
  defaultLang: string,
): TrainingLocDraft {
  const touch = (loc: LocFieldDraft): LocFieldDraft => {
    const v = loc.valuesByLang[defaultLang]?.trim() ?? "";
    if (!v) return loc;
    if (loc.dirtyLangs.includes(defaultLang)) return loc;
    return { ...loc, dirtyLangs: [...loc.dirtyLangs, defaultLang] };
  };
  return {
    ...draft,
    title: touch(draft.title),
    section: touch(draft.section),
    transcript: touch(draft.transcript),
    videoCommentary: touch(draft.videoCommentary),
    questions: draft.questions.map((q) => ({
      ...q,
      text: touch(q.text),
      choices: q.choices.map((c) => ({ ...c, loc: touch(c.loc) })),
    })),
  };
}

export function questionsToApiPayload(
  questions: QuestionDraft[],
  defaultLang: string,
): CertificationTrainingQuestionInput[] {
  return questions.map((q, i) => ({
    id: q.id,
    text: q.text.key,
    choices: q.choices.map((c) => c.loc.key),
    correctChoiceIndex: q.correctChoiceIndex,
    sortOrder: q.sortOrder ?? i,
  }));
}

export function getLocDisplayValue(
  loc: LocFieldDraft,
  lang: string,
  defaultLang: string,
): string {
  if (loc.valuesByLang[lang] != null) return loc.valuesByLang[lang];
  return loc.valuesByLang[defaultLang] ?? "";
}

/**
 * First question id whose default-language text (trimmed) duplicates an earlier
 * question. Empty texts are ignored — required-field validation covers those.
 */
export function findDuplicateQuestionTextId(
  questions: Array<{ id: string; text: LocFieldDraft }>,
  defaultLang: string,
): string | null {
  const seen = new Set<string>();
  for (const q of questions) {
    const text = getLocDisplayValue(q.text, defaultLang, defaultLang).trim();
    if (!text) continue;
    if (seen.has(text)) return q.id;
    seen.add(text);
  }
  return null;
}

export function mergeDefinitionsIntoDraft(
  draft: TrainingLocDraft,
  lang: string,
  definitions: Record<string, string>,
): TrainingLocDraft {
  const merge = (loc: LocFieldDraft): LocFieldDraft => {
    if (loc.valuesByLang[lang] != null && loc.dirtyLangs.includes(lang)) {
      // Keep in-progress edits.
      return loc;
    }
    const fromDefs = definitions[loc.key];
    if (fromDefs == null && loc.valuesByLang[lang] != null) return loc;
    return {
      ...loc,
      valuesByLang: {
        ...loc.valuesByLang,
        [lang]: fromDefs ?? loc.valuesByLang[lang] ?? "",
      },
    };
  };
  return {
    ...draft,
    title: merge(draft.title),
    section: merge(draft.section),
    transcript: merge(draft.transcript),
    videoCommentary: merge(draft.videoCommentary),
    questions: draft.questions.map((q) => ({
      ...q,
      text: merge(q.text),
      choices: q.choices.map((c) => ({ ...c, loc: merge(c.loc) })),
    })),
  };
}
